import React from 'react';
import { styled } from 'styled-components';

import { StyledLabel } from '../../Components/Label/index.tsx';

export interface IInlineGroup extends React.PropsWithChildren {
  $pseudoLabel?: boolean;
}

const InlineGroup = styled.div<IInlineGroup>`
  height: 38px;
  display: flex;
  flex-wrap: wrap;
  margin-top: ${({ $pseudoLabel }) => $pseudoLabel && '26px'};
  > ${StyledLabel} {
    margin-bottom: 0;
  }
  > * {
    cursor: pointer;
    &:not(:last-child) {
      margin-right: 15px;
    }
  }
`;

export const Label = styled.span`
  color: ${({ theme }) => theme.colors.grey100_1};
`;

export const Container = styled.label<{ $isDisabled?: boolean }>`
  margin-bottom: 0;
  display: inline-flex;
  line-height: 1.35;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  color: ${({ $isDisabled, theme }) => $isDisabled && theme.colors.grey100_1};
  &:hover > input + span {
    border-color: ${({ $isDisabled, theme }) => !$isDisabled && theme.colors.grey70_1};
  }
  > input {
    display: none;
    + span {
      position: relative;
      display: inline-flex;
      width: 18px;
      height: 18px;
      border: 1px solid ${({ theme }) => theme.colors.grey10_1};
      background: ${({ $isDisabled, theme }) => ($isDisabled ? theme.colors.grey05_3 : theme.colors.grey00_7)};
      border-radius: 100%;
      margin-right: 5px;
      transition: all 100ms;
      flex: 0 0 auto;
      &:after {
        content: '';
        width: 12px;
        height: 12px;
        background: ${({ theme }) => theme.colors.main};
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        margin: auto;
        border-radius: 100%;
        transition: all 0.2s ease;
      }
    }
    &:not(:checked) + span:after {
      opacity: 0;
      transform: scale(0);
    }
    &:checked + span:after {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export interface IRadioWrapper extends Omit<React.HTMLProps<HTMLInputElement>, 'css'> {
  dataTestId?: string;
}

const RadioWrapper: React.FC<IRadioWrapper> = ({ dataTestId, children, className, ...props }) => {
  const { disabled } = props;
  return (
    <Container className={className} data-testid={dataTestId} $isDisabled={disabled}>
      <input type='radio' {...props} />
      <span />
      {children}
    </Container>
  );
};

export type RadioFC<P = Record<string, unknown>> = React.FC<P> & {
  InlineGroup: React.FC<IInlineGroup>;
};

const RadioMemo = React.memo(RadioWrapper);
const Radio: RadioFC<IRadioWrapper> = (props) => <RadioMemo {...props} />;

Radio.InlineGroup = InlineGroup;

export { Radio };
